/**
 * ShopPage — the Prompt-Brush shop (prints, apparel & objects).
 *
 * Everything is sold from one shop now — delcan.co. Each card here links to the
 * same good there (`href`), tagged so delcan.co's order emails say it came from
 * prompt-brush.com. (Earlier: each product linked to its own Stripe Payment Link (created in the Stripe
 * dashboard — no API keys live in the app). Stripe hosts the checkout, collects the
 * shipping address, and pays Pablo's Stripe account directly. Fulfillment is a mix:
 * prints & apparel via print-on-demand (Printful), unique objects shipped by hand.
 *
 * To add a product: create a Payment Link in Stripe, drop a photo in /public/shop/<id>.jpg,
 * and add an entry to PRODUCTS below with that link. An empty `paymentLink` shows "Coming soon".
 *
 * Typography mirrors BookPage: Delcan Mono display, FK Grotesk Mono labels, serif for prose.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ZineLead } from '../components/ZineLead';

const MONO = 'FK Grotesk Mono, monospace';
const DISPLAY = 'Delcan Mono, monospace';
const SERIF = 'Martina Plantijn, Georgia, serif';
const DELCAN_SIZE = 'text-2xl';

type Product = {
  id: string;
  name: string;
  kind: 'Book' | 'Zine' | 'Print' | 'Poster' | 'Apparel' | 'Object';
  blurb?: string;
  price: string; // display only, e.g. '$40'
  image: string; // /public/shop/<id>.jpg
  paymentLink: string; // Stripe Payment Link URL — leave '' for "Coming soon"
  to?: string; // internal route (e.g. /collection) — used as the CTA instead of paymentLink
  href?: string; // the good on delcan.co, where it's bought
  cta?: string; // custom button label
  soldOut?: boolean;
  draft?: boolean; // planned, but not shown until there's a photo and a link
};

const PRODUCTS: Product[] = [
  {
    // The book was sold only from /book, which meant the store didn't stock the
    // one thing most people come here for. It keeps its own page for the essay
    // and the reviews; this card is the way to it from the store.
    id: 'book',
    name: 'Prompt-Brush 1.0',
    kind: 'Book',
    blurb: 'The book. 1,541 prompts sent in by the public, every one drawn by hand. Hardcover, slipcased.',
    price: '$30',
    image: '/book-cover.jpg',
    paymentLink: '',
    href: 'https://delcan.co/shop/prompt-brush-1-0-book?utm_source=prompt-brush.com&utm_medium=referral',
    cta: 'Buy the book · $30 \u2192',
  },
  {
    id: 'giclee-print',
    name: 'Giclée prints',
    kind: 'Print',
    blurb: 'Any drawing from the archive as a signed 13 × 17 in archival giclée. $45.',
    price: '$45',
    image: '/shop/giclee-print.jpg',
    paymentLink: '',
    href: 'https://delcan.co/prints?utm_source=prompt-brush.com&utm_medium=referral',
    cta: 'Choose a print · $45 →',
  },
  {
    id: 'set',
    name: 'Prompt-Brush Set',
    kind: 'Object',
    blurb: 'The book, the zine, the Box Man patch and a signed print of the drawing you choose. Free shipping.',
    price: '$90',
    image: 'https://delcan.co/store/prompt-brush-set/set-card.jpg',
    paymentLink: '',
    href: 'https://delcan.co/prints/659?set=1&utm_source=prompt-brush.com&utm_medium=referral',
    cta: 'Build your set · $90 →',
  },
  {
    id: 'sva-poster',
    name: 'S.V.A. Subway Poster',
    kind: 'Poster',
    blurb: '30 × 46 in. Signed poster from the 2025 NYC Subway campaign for the School of Visual Arts and Prompt-Brush 1.0.',
    price: '$50',
    image: '/shop/sva-poster.jpg',
    paymentLink: '',
    href: 'https://delcan.co/shop/prompt-brush-10-sva-poster?utm_source=prompt-brush.com&utm_medium=referral',
    cta: 'Buy · $50 →',
  },
  {
    id: 'logo-tee',
    draft: true,
    name: 'Non-AI Tee',
    kind: 'Apparel',
    blurb: 'Heavyweight cotton, printed on demand.',
    price: '$35',
    image: '/shop/logo-tee.jpg',
    paymentLink: '',
  },
  {
    id: 'sticker-pack',
    draft: true,
    name: 'Sticker Pack',
    kind: 'Object',
    blurb: 'Five hand-drawn vinyl stickers.',
    price: '$10',
    image: '/shop/sticker-pack.jpg',
    paymentLink: '',
  },
];

function ProductImage({ src, name }: { src: string; name: string }) {
  const [errored, setErrored] = useState(false);
  if (errored || !src) {
    return (
      <div
        className="w-full rounded-lg border border-border bg-card flex items-center justify-center"
        style={{ aspectRatio: '1/1' }}
      >
        <span className="text-muted-foreground uppercase tracking-widest" style={{ fontSize: '9pt', fontFamily: MONO }}>
          Photo soon
        </span>
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={name}
      className="w-full rounded-lg border border-border bg-card"
      style={{ aspectRatio: '1/1', objectFit: 'cover' }}
      onError={() => setErrored(true)}
    />
  );
}

function ProductCard({ p }: { p: Product }) {
  const buyable = !!p.paymentLink && !p.soldOut;
  return (
    <div className="flex flex-col">
      <ProductImage src={p.image} name={p.name} />
      <div className="flex items-baseline justify-between mt-4">
        <div className={`text-foreground ${DELCAN_SIZE}`} style={{ fontFamily: DISPLAY }}>
          {p.name}
        </div>
        {!!p.price && (
          <div className="text-foreground ml-3 shrink-0" style={{ fontFamily: MONO, fontSize: '12pt' }}>
            {p.price}
          </div>
        )}
      </div>
      <div className="text-muted-foreground uppercase tracking-widest mt-2" style={{ fontSize: '9pt', fontFamily: MONO }}>
        {p.kind}
      </div>
      {!!p.blurb && (
        <p className="text-muted-foreground leading-snug mt-2" style={{ fontFamily: MONO, fontSize: '10.5pt' }}>
          {p.blurb}
        </p>
      )}
      {p.href ? (
        <a
          href={p.href}
          className="block w-full text-center border border-border rounded-full px-6 py-3 mt-4 text-foreground hover:bg-foreground hover:text-background transition-colors"
          style={{ fontFamily: MONO, fontSize: '11pt' }}
        >
          {p.cta || `Buy · ${p.price} →`}
        </a>
      ) : p.to ? (
        <Link
          to={p.to}
          className="block w-full text-center border border-border rounded-full px-6 py-3 mt-4 text-foreground hover:bg-foreground hover:text-background transition-colors"
          style={{ fontFamily: MONO, fontSize: '11pt' }}
        >
          {p.cta || 'View →'}
        </Link>
      ) : buyable ? (
        <a
          href={p.paymentLink}
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full text-center border border-border rounded-full px-6 py-3 mt-4 text-foreground hover:bg-foreground hover:text-background transition-colors"
          style={{ fontFamily: MONO, fontSize: '11pt' }}
        >
          {p.cta || `Buy · ${p.price}`}
        </a>
      ) : (
        <div
          className="block w-full text-center border border-border rounded-full px-6 py-3 mt-4 text-muted-foreground uppercase tracking-widest cursor-default"
          style={{ fontFamily: MONO, fontSize: '9pt' }}
        >
          {p.soldOut ? 'Sold out' : 'Coming soon'}
        </div>
      )}
    </div>
  );
}

export function ShopPage() {
  return (
    <div className="h-screen overflow-y-auto">
      <div className="container mx-auto px-4 py-16 md:py-24 max-w-5xl">
        {/* ===== Header ===== */}
        <div className="max-w-2xl">
          <div className="text-muted-foreground uppercase tracking-widest mb-4" style={{ fontSize: '9pt', fontFamily: MONO }}>
            The Store
          </div>
          <h1 className={`text-foreground ${DELCAN_SIZE} leading-tight mb-3`} style={{ fontFamily: DISPLAY }}>
            The book, the zine, prints &amp; posters
          </h1>
          <p className="text-foreground leading-relaxed max-w-md" style={{ fontFamily: SERIF, fontSize: '15pt' }}>
            Take a piece of Prompt-Brush home. Everything is made by hand, never by AI — and every
            order helps keep this independent project going. The shop lives at{' '}
            <a href="https://delcan.co/shop?utm_source=prompt-brush.com&utm_medium=referral" className="underline">delcan.co</a>, Pablo Delcan's studio.
          </p>
        </div>

        {/* ===== The zine, up top — it's the only thing here that IS Prompt-Brush ===== */}
        <ZineLead />

        {/* ===== Everything else ===== */}
        <div className="mt-20 text-muted-foreground uppercase tracking-widest" style={{ fontSize: '9pt', fontFamily: MONO }}>
          Also in the store
        </div>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-12">
          {PRODUCTS.filter((p) => !p.draft).map((p) => (
            <ProductCard key={p.id} p={p} />
          ))}
        </div>

        {/* ===== Footer note ===== */}
        <div className="mt-20 pt-10 border-t border-border max-w-2xl">
          <p className="text-muted-foreground leading-relaxed" style={{ fontFamily: MONO, fontSize: '10.5pt' }}>
            Checkout is handled securely by Stripe, which collects your shipping address. The zine
            ships flat-rate; prints are made to order. Questions? Email{' '}
            <a href="mailto:hello@prompt-brush.com" className="underline">
              hello@prompt-brush.com
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
}
